import { useState, useEffect } from "react";
import { ArrowLeft, Menu, X, Search, Bell } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { triggerHaptic } from "@/hooks/useHaptic";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { MobileSearchDrawer } from "@/components/mobile/MobileSearchDrawer";

interface MobileHeaderProps {
  title?: string;
  showBack?: boolean;
  onMenuClick?: () => void;
  isMenuOpen?: boolean;
  transparent?: boolean;
  breadcrumb?: string;
}

export function MobileHeader({
  title,
  showBack = true,
  onMenuClick,
  isMenuOpen,
  transparent = false,
  breadcrumb,
}: MobileHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [alertsCount, setAlertsCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  // Pages that should not show back button (main pages)
  const mainPages = [
    '/dashboard',
    '/projects',
    '/financeiro',
    '/equipe',
    '/alertas',
    '/configuracoes',
    '/agente-ia',
    '/documentos'
  ];
  const isMainPage = mainPages.includes(location.pathname);

  // Fetch alerts count
  useEffect(() => {
    if (!user) return;

    const fetchAlerts = async () => {
      try {
        const { data: lembretes } = await supabase
          .from('lembretes')
          .select('data, status')
          .eq('user_id', user.id)
          .eq('status', 'pendente');

        if (lembretes) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const threeDaysFromNow = new Date(today);
          threeDaysFromNow.setDate(today.getDate() + 3);

          const count = lembretes.filter(item => {
            const dataLembrete = new Date(item.data);
            dataLembrete.setHours(0, 0, 0, 0);
            return dataLembrete <= threeDaysFromNow;
          }).length;

          setAlertsCount(count);
        }
      } catch (error) {
        console.error('Error fetching alerts:', error);
      }
    };

    fetchAlerts();
  }, [user]);

  const handleBack = () => {
    triggerHaptic('light');
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/dashboard');
    }
  };

  const handleMenuClick = () => {
    triggerHaptic('light');
    onMenuClick?.();
  };

  const handleSearchClick = () => {
    triggerHaptic('light');
    setSearchOpen(true);
  };

  const handleAlertsClick = () => {
    triggerHaptic('light');
    navigate('/alertas');
  };

  const handleProfileClick = () => {
    triggerHaptic('light');
    navigate('/configuracoes');
  };

  const getUserInitials = () => {
    if (user?.user_metadata?.full_name) {
      const names = user.user_metadata.full_name.split(' ');
      return names.length > 1
        ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
        : names[0][0].toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || 'U';
  };

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.3 }}
        className={cn(
          "md:hidden fixed top-0 left-0 right-0 z-50 safe-area-top",
          transparent
            ? "bg-transparent"
            : "glass-panel"
        )}
      >
        <div className="flex items-center justify-between h-14 px-3">
          {/* Left side - Back button or Menu */}
          <div className="flex items-center">
            {showBack && !isMainPage ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleBack}
                className={cn(
                  "h-9 w-9 rounded-full",
                  "active:scale-[0.9] transition-transform duration-200",
                  "bg-secondary/50 hover:bg-secondary"
                )}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
            ) : (
              // Espaçador invisível para manter layout quando não há botão
              <div className="w-9 h-9" />
            )}
          </div>

          {/* Center - Logo or Title with optional breadcrumb */}
          <div className="flex-1 flex flex-col items-center justify-center px-2">
            {title ? (
              <>
                <motion.h1
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-base font-semibold text-foreground truncate max-w-[180px]"
                >
                  {title}
                </motion.h1>
                {breadcrumb && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="text-[10px] text-muted-foreground truncate max-w-[150px]"
                  >
                    {breadcrumb}
                  </motion.span>
                )}
              </>
            ) : (
              <motion.img
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                alt="Obrim"
                src="/lovable-uploads/73e4b61d-e4f1-443a-bcb7-800408ad6415.png"
                className="h-10 w-auto"
                width={40}
                height={40}
              />
            )}
          </div>

          {/* Right side - Search, Notifications, Profile */}
          <div className="flex items-center gap-2">
            {/* Search Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSearchClick}
              className="h-9 w-9 rounded-full bg-secondary/50 hover:bg-secondary"
            >
              <Search className="h-4.5 w-4.5 text-muted-foreground" />
            </Button>

            {/* Notifications Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleAlertsClick}
              className="relative h-9 w-9 rounded-full bg-secondary/50 hover:bg-secondary"
            >
              <Bell className="h-4.5 w-4.5 text-muted-foreground" />
              <AnimatePresence>
                {alertsCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className={cn(
                      "absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1",
                      "flex items-center justify-center",
                      "text-[10px] font-bold",
                      "bg-destructive text-destructive-foreground rounded-full"
                    )}
                  >
                    {alertsCount > 9 ? '9+' : alertsCount}
                  </motion.span>
                )}
              </AnimatePresence>
            </Button>

            {/* Profile Avatar */}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleProfileClick}
              className="h-9 w-9 rounded-full p-0 overflow-hidden"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={user?.user_metadata?.avatar_url} />
                <AvatarFallback className="text-xs bg-primary/10 text-primary">
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
            </Button>
          </div>
        </div>
      </motion.header>

      {/* Search Drawer */}
      <MobileSearchDrawer open={searchOpen} onOpenChange={setSearchOpen} />
    </>
  );
}
